import {
    Column,
    Container,
    cssVar,
    Divider,
    GenericModal,
    Grid,
    Header,
    Label,
    Link,
    Row,
    Separator,
    Spinner,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

const ModelContainer = styled.div`
    height: 50vh;
    overflow-y: auto;
    border: 1px solid #000;
`;

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const CopiedLabel = styled(Label)`
    transform: scale(1);
    transition: all 0.5s;
    animation: 0.5s ${fadeIn} ease-in-out
`;

const CallContainer = styled.div`
    ${({ inline }) =>
        inline
            ? `
        display: contents;
        `
            : 'margin-right: 40px;'}
`;

const ProfileLogs = (props) => {
    const sizes = useSizes();
    const alignments = useAlignments();
    const screen = useScreen();

    const [displayModel, setDisplayModel] = useState(false);
    const [modelId, setModelId] = useState(null);
    const [modelType, setModelType] = useState(null);
    const [textCopied, setTextCopied] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        props.doGetPolicy();
        const logAction = { referenceCode: props?.policy?.guid, productCode: props?.policy?.product };
        props.doGetLogs(logAction);
    }, []);

    const closeModel = () => {
        setDisplayModel(false);
        setModelId(null);
        setModelType(null);
    };

    const openModel = (referenceCode, type) => {
        setModelId(referenceCode);
        setDisplayModel(true);
        setModelType(type);
        setTextCopied(false);
    };

    const copyText = async (text) => {
        await navigator.clipboard.writeText(text);
        setTextCopied(true);
    };

    const getModel = () => {
        const content = props.policyLogs.find((log) => log.eventCode === modelId);
        const logContent = content.logContent;
        return (
            <GenericModal
                animate
                visible
                closeEvent={() => closeModel()}
                size={sizes.lg}
                items={
                    <React.Fragment>
                        <Header size={sizes.md} text={modelType} bottom={sizes.md} />
                        <ModelContainer>
                            {logContent ? <Label textAlign={alignments.left} text={logContent} /> : <Spinner />}
                        </ModelContainer>
                        {textCopied && (
                            <CopiedLabel
                                top={sizes.md}
                                textAlign={alignments.center}
                                color={cssVar.colorSuccess}
                                text={<FormattedMessage id='Profile_Logs.textCopied' />}
                            />
                        )}
                    </React.Fragment>
                }
                actionButtons={[
                    {
                        primary: true,
                        text: <FormattedMessage id='Profile_Logs.close' />,
                        clickEvent: () => closeModel(),
                    },
                    {
                        primary: false,
                        text: <FormattedMessage id='Profile_Logs.copy' />,
                        clickEvent: () => copyText(logContent),
                    },
                ]}
            />
        );
    };

    const mobile = screen?.isMobile;

    return (
        <Container top={sizes.sm} bottom={sizes.xl}>
            <Grid>
                {/*Page Header*/}
                <Row>
                    <Column>
                        <Container verticalAlignment={alignments.center} top={sizes.md} fullSize>
                            <Divider />
                            <Header
                                size={sizes.xl}
                                textAlign={alignments.left}
                                text={<FormattedMessage id='Profile_Logs.apiLogs' />}
                            />
                        </Container>
                    </Column>
                    <Column nogutter>
                        <Separator orientation='horizontal' top={sizes.sm} bottom={sizes.sm} solid />
                    </Column>
                </Row>
                {/*Column Header*/}
                {props.policyLogs && (
                    <Row>
                        <Column xs={4} md={4}>
                            <Container verticalAlignment={alignments.center} top={sizes.md} fullSize>
                                <Header
                                    size={sizes.md}
                                    textAlign={alignments.left}
                                    text={<FormattedMessage id='Profile_Logs.event' />}
                                />
                            </Container>
                        </Column>
                        {mobile ? (
                            <Column xs={4} md={2}>
                                <Container verticalAlignment={alignments.center} top={sizes.md} fullSize>
                                    <Header
                                        size={sizes.md}
                                        textAlign={alignments.left}
                                        text={<FormattedMessage id='Profile_Logs.calls' />}
                                    />
                                </Container>
                            </Column>
                        ) : (
                            <React.Fragment>
                                <Column xs={2} md={2}>
                                    <Container verticalAlignment={alignments.center} top={sizes.md} fullSize>
                                        <Header
                                            size={sizes.md}
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id='Profile_Logs.request' />}
                                        />
                                    </Container>
                                </Column>
                                <Column xs={2} md={2}>
                                    <Container verticalAlignment={alignments.center} top={sizes.md} fullSize>
                                        <Header
                                            size={sizes.md}
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id='Profile_Logs.response' />}
                                        />
                                    </Container>
                                </Column>
                                <Column xs={2} md={2}>
                                    <Container verticalAlignment={alignments.center} top={sizes.md} fullSize>
                                        <Header
                                            size={sizes.md}
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id='Profile_Logs.error' />}
                                        />
                                    </Container>
                                </Column>
                            </React.Fragment>
                        )}
                        <Column xs={mobile ? 4 : 2} md={2}>
                            <Container verticalAlignment={alignments.center} top={sizes.md} fullSize>
                                <Header
                                    size={sizes.md}
                                    textAlign={alignments.left}
                                    text={<FormattedMessage id='Profile_Logs.timestampUtc' />}
                                />
                            </Container>
                        </Column>
                    </Row>
                )}
                {/*Row Logs*/}
                {props.policyLogs && props.policyLogs.length > 0 ? (
                    props.policyLogs.map((log, idx) => {
                        const eventCode = log.eventCode;
                        const timeStampArr = log.timeStamp.split('T');
                        const date = timeStampArr[0];
                        const time = timeStampArr[1];
                        return (
                            // biome-ignore lint/suspicious/noArrayIndexKey: log entries are append-only and ordered by timestamp
                            <Row key={`Log-${idx}`}>
                                <Column xs={4} md={4}>
                                    <Container verticalAlignment={alignments.center} top={sizes.md} fullSize>
                                        <Label textAlign={alignments.left} bold text={log.sourceType} />
                                        <Label textAlign={alignments.left} size={sizes.sm} text={eventCode} />
                                    </Container>
                                </Column>
                                <CallContainer inline={!mobile}>
                                    <Column xs={2} md={2}>
                                        <Container
                                            verticalAlignment={alignments.center}
                                            top={sizes.md}
                                            clickEvent={() => {
                                                if (log.request) {
                                                    props.doGetLogContent({ eventCode, logName: log.request });
                                                    openModel(eventCode, 'Request');
                                                }
                                            }}
                                            fullSize
                                        >
                                            <Link
                                                textAlign={alignments.left}
                                                text={log.request ? <FormattedMessage id='Profile_Logs.request' /> : ''}
                                            />
                                        </Container>
                                    </Column>
                                    <Column xs={2} md={2}>
                                        <Container
                                            verticalAlignment={alignments.center}
                                            top={sizes.md}
                                            clickEvent={() => {
                                                if (log.response) {
                                                    props.doGetLogContent({ eventCode, logName: log.response });
                                                    openModel(eventCode, 'Response');
                                                }
                                            }}
                                            fullSize
                                        >
                                            <Link
                                                textAlign={alignments.left}
                                                text={
                                                    log.response ? <FormattedMessage id='Profile_Logs.response' /> : ''
                                                }
                                            />
                                        </Container>
                                    </Column>
                                    <Column xs={2} md={2}>
                                        <Container
                                            verticalAlignment={alignments.center}
                                            top={sizes.md}
                                            clickEvent={() => {
                                                if (log.responseError || log.error) {
                                                    props.doGetLogContent({
                                                        eventCode,
                                                        logName: log.responseError || log.error,
                                                    });
                                                    openModel(eventCode, 'Error');
                                                }
                                            }}
                                            fullSize
                                        >
                                            <Link
                                                textAlign={alignments.left}
                                                text={
                                                    log.responseError || log.error ? (
                                                        <FormattedMessage id='Profile_Logs.error' />
                                                    ) : (
                                                        ''
                                                    )
                                                }
                                            />
                                        </Container>
                                    </Column>
                                </CallContainer>
                                <Column xs={mobile ? 4 : 2} md={2}>
                                    <Container
                                        verticalAlignment={mobile ? '' : alignments.center}
                                        top={sizes.md}
                                        fullSize
                                    >
                                        <Label textAlign={alignments.left} text={date} />
                                        <Label textAlign={alignments.left} text={time} />
                                    </Container>
                                </Column>
                            </Row>
                        );
                    })
                ) : (
                    <Label
                        textAlign={alignments.center}
                        top={sizes.md}
                        text={<FormattedMessage id='Profile_Logs.noLogs' />}
                    />
                )}
                {/*Action Model*/}
                {displayModel && getModel()}
            </Grid>
        </Container>
    );
};

export default ProfileLogs;
